import { cn } from "@lib/cn"
import { Content, Portal, Root, Trigger } from "@radix-ui/react-dropdown-menu"
import React, { FC, PropsWithChildren } from "react"
import IconChevronDown from "./icons/IconChevronDown"
import { Span } from "./typograhpy/Span"

type DefaultVariant = {
  trigger?: never
  title: string
}

type CustomTriggerVariant = {
  trigger: React.ReactNode
  title?: never
}

export type TriggerType = DefaultVariant | CustomTriggerVariant

type DropdownAlign = "start" | "center" | "end"

export type DropdownProps = PropsWithChildren<
  TriggerType & {
    /**
     * When true (default), the dropdown behaves like a modal and blocks outside interactions.
     * Set to false to allow interacting with the rest of the UI while the dropdown is open.
     */
    modal?: boolean
    /** Horizontal alignment relative to the trigger. Defaults to "end" (current behavior). */
    align?: DropdownAlign
  }
>

export const Dropdown: FC<DropdownProps> = ({
  trigger,
  title,
  children,
  modal = true,
  align = "end",
}) => {
  return (
    <Root modal={modal}>
      <Trigger
        className={
          "group focus-visible:ring-shade-300/50 focus-visible:rounded-[1.125rem] focus-visible:ring focus-visible:outline-0"
        }
        asChild
      >
        {title ? (
          <span
            className={cn(
              "text-shade-500 flex items-center rounded-[3px] transition-all duration-[0.25s]",
              "hover:text-shade-400 focus-visible:dropdown-trigger-ring focus:ring-0 focus:outline-0 focus-visible:outline-0 active:scale-[0.98]",
            )}
          >
            <Span variant="button-label-big" className="mr-1 block">
              {title}
            </Span>
            <IconChevronDown
              data-chevron
              size={12}
              className="group-data-[state=open]:rotate-180"
            />
          </span>
        ) : (
          trigger
        )}
      </Trigger>
      <Portal>
        <Content
          className="animate-dropdown-fade-in border-border bg-background z-99 min-w-64 overflow-hidden rounded-b border"
          align={align}
          side="bottom"
          sideOffset={15}
        >
          {children}
        </Content>
      </Portal>
    </Root>
  )
}
